import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import axios from 'axios';
import { BrowserHistory } from 'history';
import { useTranslation } from 'react-i18next';
import Event from '../../lib/EventEmitter';
import { Namespace } from '../../translation/namespace';
import { State } from '../../application/browser/browser.utils';

type FormValidationObject = {
  id: string;
  isDirty: () => boolean;
};

const registeredForms = new Set<FormValidationObject>();

function isAnyFormDirty() {
  return Array.from(registeredForms).some(form => form.isDirty());
}

export async function sendPageLockRelease(editModeActive: boolean | undefined, pageLockReleaseUrl: string | undefined) {
  if (editModeActive) {
    try {
      if (pageLockReleaseUrl) {
        await axios.post(pageLockReleaseUrl);
      }
    } catch {
      // ignore
    }
  }
}

export function useLeavingGuard(
  history: BrowserHistory,
  state: State,
  setState: Dispatch<SetStateAction<State>>,
  pageLockReleaseUrl: string | undefined,
  pageId: string | undefined,
  userId: string | undefined,
  editModeActive: boolean | undefined
) {
  const { t } = useTranslation(Namespace.phrases);

  const unsavedChanges = t(`${Namespace.phrases}:unsavedChanges`);

  React.useLayoutEffect(
    () =>
      history.listen(event => {
        if (event.location.pathname !== state.location.pathname) {
          if (!isAnyFormDirty() || window.confirm(unsavedChanges)) {
            sendPageLockRelease(editModeActive, pageLockReleaseUrl);
            setState(event);
          } else if (event.action === 'REPLACE') {
            history.replace(state.location.pathname);
          } else {
            history.back();
          }
        } else {
          setState(event);
        }
      }),
    [history, state.location, setState, editModeActive, pageLockReleaseUrl, pageId, userId, unsavedChanges]
  );

  const interceptUnload = useCallback(
    (event: Event): string | undefined => {
      if (isAnyFormDirty()) {
        event.preventDefault();
        // we need to set `event.returnValue = true` for Chrome|ium Browsers
        // NOTE that MDN sets the returnValue to a string but TS expects a boolean.
        // Both seem to work in Chromium
        event.returnValue = true;
        // According to MDN some browsers activate the prompt when returning a string
        return unsavedChanges;
      }

      // send page release?
    },
    [unsavedChanges]
  );

  useEffect(() => {
    Event.register(Event.prompt.addToQueue, (form: FormValidationObject) => {
      registeredForms.add(form);
    });
    Event.register(Event.prompt.removeFromQueue, (id: string) => {
      registeredForms.forEach(form => form.id === id && registeredForms.delete(form));
    });

    return () => {
      Event.remove(Event.prompt.addToQueue);
      Event.remove(Event.prompt.removeFromQueue);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', interceptUnload);

    return () => {
      window.removeEventListener('beforeunload', interceptUnload);
    };
  }, [interceptUnload]);
}
